import { useEffect, useRef, useState } from "react";
import ButtonComponent from "../../../components/Comman/ButtonComponent";
import TypographyCompo from "../../../components/Comman/TypographyCompo";
import { allCheckPassedMsg, allValidationPassedMsg, doNotCloseValdationWindowMsg, physicalEntryErrorMsg, submmisionBlockedMsg, validationFailedMsg, validationHaltedMsg, validationRunningMsg, warningFoundMsg } from "../../../config/messageconstant";
import { ArrowLeftIcon, CheckCircleIcon, ErrorIcon, FilledTick, SaveIcon, ShieldIcon, SpinnerIcon, WarnIcon } from "../icons/Icons";
import { DEFAULT_STEPS, GLOBAL_CSS_VALIDATION, validationStyles as styles, validationModalStyleconstT } from "./ChallanSetllementConstants";

// ─── Step Dot ─────────────────────────────────────────────────────────────────

const StepDot = ({ state }) => {
    const variantStyle = {
        ok: styles.dotOk,
        warn: styles.dotWarn,
        err: styles.dotErr,
        idle: styles.dotIdle,
        running: styles.dotRunning,
    };
    const iconMap = {
        ok: <FilledTick size={13} />,
        running: <SpinnerIcon />,
        idle: null,
    };
    return (
        <div
            style={{ ...styles.dotBase, ...(variantStyle[state] ?? styles.dotIdle) }}
        >
            {iconMap[state] ?? null}
        </div>
    );
};

const ValidationStep = ({ step, isLast, halted }) => {
    const { name, description, state } = step;
    const lineColor = { ok: validationModalStyleconstT.ok, warn: validationModalStyleconstT.warn, err: validationModalStyleconstT.err }[state] ?? validationModalStyleconstT.lineIdle;
    const dimmed = halted && state === "idle";

    return (
        <div
            style={{
                ...styles.vStep,
                opacity: dimmed ? 0.38 : 1,
                transition: "opacity 0.3s ease",
            }}
        >
            <div style={styles.stepTrack}>
                <StepDot state={state} />
                {!isLast && (
                    <div style={{ ...styles.stepLine, background: lineColor }} />
                )}
            </div>
            <div style={styles.stepContent}>
                <div style={styles.stepName}>{name}</div>
                <div
                    style={{ ...styles.stepDesc, ...(dimmed ? { color: validationModalStyleconstT.ink4 } : {}) }}
                >
                    {dimmed
                        ? validationHaltedMsg
                        : description}
                </div>
                {/* Red warning note — only shown when this step has an error */}
                {state === "err" && (
                    <div style={styles.stepErrNote}>
                        <WarnIcon size={13} />
                        <TypographyCompo>{physicalEntryErrorMsg}</TypographyCompo>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Status Banner ────────────────────────────────────────────────────────────

const StatusBanner = ({ phase }) => {
    const cfg = {
        running: {
            style: styles.bannerRunning,
            icon: <TypographyCompo style={styles.pulseDot} />,
            text: validationRunningMsg,
        },
        ok: {
            style: styles.bannerOk,
            icon: <CheckCircleIcon size={15} />,
            text: allCheckPassedMsg,
        },
        warn: {
            style: styles.bannerWarn,
            icon: <WarnIcon size={15} />,
            text: warningFoundMsg,
        },
        err: {
            style: styles.bannerErr,
            icon: <ErrorIcon size={15} />,
            text: validationFailedMsg,
        },
    }[phase] ?? {};

    return (
        <div style={{ ...styles.banner, ...cfg.style }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                }}
            >
                {cfg.icon}
            </div>

            <TypographyCompo
                style={{
                    margin: 0,
                    flex: 1,
                    lineHeight: "18px",
                }}
            >
                {cfg.text}
            </TypographyCompo>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
//  ValidationModal
// ═══════════════════════════════════════════════════════════════════════════════

const ValidationModal = ({
    open,
    onClose,
    onSave,
    challanNo = "",
    driverName = "",
    settledBy = "",
    steps = DEFAULT_STEPS,
    hasPhysicalReturn = true,
}) => {
    const [stepRows, setStepRows] = useState(() =>
        steps.map((s) => ({ ...s, state: "idle" })),
    );
    const [phase, setPhase] = useState("running");
    const [saveShow, setSaveShow] = useState(false);
    const started = useRef(false);

    useEffect(() => {
        if (!open) {
            started.current = false;
            return;
        }
        if (started.current) return;
        started.current = true;

        // When physical return is missing: only run Step 1 as "err" and halt — do NOT proceed to further steps
        if (!hasPhysicalReturn) {
            const stockStep = { ...steps[0], state: "idle" };
            const restIdle = steps.slice(1).map((s) => ({ ...s, state: "idle" }));
            setStepRows([stockStep, ...restIdle]);
            setPhase("running");
            setSaveShow(false);

            // Show Step 1 as "running" briefly, then resolve as "err" and stop
            setTimeout(() => {
                setStepRows((prev) =>
                    prev.map((r, i) => (i === 0 ? { ...r, state: "running" } : r)),
                );
            }, 350);

            setTimeout(() => {
                setStepRows((prev) =>
                    prev.map((r, i) => (i === 0 ? { ...r, state: "err" } : r)),
                );
                setPhase("err");
                // Steps 2 & 3 remain "idle" — validation halted here
            }, 350 + steps[0].delay);

            return; // ← stops here, no further steps scheduled
        }

        // Normal flow — physical return is present, run all steps
        setStepRows(steps.map((s) => ({ ...s, state: "idle" })));
        setPhase("running");
        setSaveShow(false);

        let delay = 350,
            warns = 0,
            errs = 0;

        steps.forEach((step, idx) => {
            const startAt = delay;
            const endAt = delay + step.delay;

            setTimeout(() => {
                setStepRows((prev) =>
                    prev.map((r, i) => (i === idx ? { ...r, state: "running" } : r)),
                );
            }, startAt);

            setTimeout(() => {
                if (step.type === "warn") warns++;
                if (step.type === "err") errs++;
                setStepRows((prev) =>
                    prev.map((r, i) => (i === idx ? { ...r, state: step.type } : r)),
                );

                if (idx === steps.length - 1) {
                    const fp = errs > 0 ? "err" : warns > 0 ? "warn" : "ok";
                    setPhase(fp);
                    if (fp !== "err") setSaveShow(true);
                }
            }, endAt);

            delay = endAt + 200;
        });
    }, [open]); // open is the only trigger — ref pattern avoids stale-closure warning

    if (!open) return null;

    const HEADER = {
        running: {
            title: "Validation in progress",
            sub: "Running master data checks — please wait",
        },
        ok: {
            title: "All checks passed!",
            sub: "Settlement is clean and ready for admin approval.",
        },
        warn: {
            title: "Checks completed with warnings",
            sub: "Review warnings before proceeding.",
        },
        err: {
            title: "Validation failed",
            sub: "One or more critical checks did not pass. Fix issues and resubmit.",
        },
    };
    const NOTE = {
        running: doNotCloseValdationWindowMsg,
        ok: allValidationPassedMsg,
        warn: warningFoundMsg,
        err: submmisionBlockedMsg,
    };
    const { title, sub } = HEADER[phase] ?? HEADER.running;
    const warnCount = stepRows.filter((r) => r.state === "warn").length;
    const errCount = stepRows.filter((r) => r.state === "err").length;

    return (
        <>
            <style>{GLOBAL_CSS_VALIDATION}</style>
            <div
                style={styles.backdrop}
                onClick={phase !== "running" ? onClose : undefined}
            >
                <div
                    style={styles.popup}
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Validation"
                >
                    {/* ── Header */}
                    <div style={styles.header}>
                        <div style={styles.headerIcon}>
                            <ShieldIcon />
                        </div>
                        <div>
                            <div style={styles.headerTitle}>{title}</div>
                            <div style={styles.headerSub}>{sub}</div>
                        </div>
                    </div>

                    {/* ── Context strip */}
                    <div style={styles.metaStrip}>
                        {[
                            { label: "Challan", value: challanNo },
                            { label: "Driver", value: driverName },
                            { label: "Settled by", value: settledBy },
                        ].map(({ label, value }, i, arr) => (
                            <div
                                key={label}
                                style={{
                                    ...styles.metaCell,
                                    ...(i < arr.length - 1
                                        ? { borderRight: `1px solid ${validationModalStyleconstT.border}` }
                                        : {}),
                                }}
                            >
                                <TypographyCompo style={styles.metaLabel}>{label}</TypographyCompo>
                                <TypographyCompo style={styles.metaValue}>{value}</TypographyCompo>
                            </div>
                        ))}
                    </div>

                    {/* ── Steps */}
                    <div style={styles.stepsWrap}>
                        {stepRows.map((row, i) => (
                            <ValidationStep
                                key={row.id}
                                step={row}
                                isLast={i === stepRows.length - 1}
                                halted={phase === "err"}
                            />
                        ))}
                    </div>

                    {/* ── Status banner */}
                    <StatusBanner
                        phase={phase}
                        warnCount={warnCount}
                        errCount={errCount}
                    />

                    {/* ── Footer */}
                    <div style={styles.footer}>
                        <TypographyCompo style={styles.footerNote}>{NOTE[phase]}</TypographyCompo>
                        <div style={styles.footerBtns}>
                            {phase !== "running" && (
                                <ButtonComponent style={styles.backBtn} onClick={onClose}>
                                    <ArrowLeftIcon /> Back
                                </ButtonComponent>
                            )}
                            {saveShow && (
                                <ButtonComponent style={styles.saveBtn} onClick={onSave}>
                                    <SaveIcon /> Settle Challan
                                </ButtonComponent>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
export default ValidationModal;




